import { Controller } from "@hotwired/stimulus";

// Kick's category search rejects anything shorter outright, and the server
// applies the same floor (StreamCommand-75yu).
const MIN_QUERY_LENGTH = 3;

function csrfToken() {
  return (document.querySelector('meta[name="csrf-token"]') || {}).content || "";
}

export default class extends Controller {
  static targets = ["input", "idField", "nameField", "dropdown", "error"];
  static values = { url: String, tenant: String };

  connect() {
    this._timer = null;
    this._requestId = 0;
    this._onOutsideClick = this._handleOutsideClick.bind(this);
    document.addEventListener("click", this._onOutsideClick);
    this.inputTarget.setAttribute("role", "combobox");
    this.inputTarget.setAttribute("aria-haspopup", "listbox");
    this.inputTarget.setAttribute("aria-expanded", "false");
    this.inputTarget.setAttribute("autocomplete", "off");
    this.dropdownTarget.setAttribute("role", "listbox");
  }

  disconnect() {
    clearTimeout(this._timer);
    document.removeEventListener("click", this._onOutsideClick);
  }

  keydown(event) {
    if (!this._items().length) return;
    if (event.key === "Escape") {
      this._closeDropdown();
      return;
    }
    if (event.key === "ArrowDown") {
      event.preventDefault();
      this._moveFocus(1);
      return;
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      this._moveFocus(-1);
      return;
    }
    if (event.key === "Enter") {
      const focused = this.dropdownTarget.querySelector('[aria-selected="true"]');
      if (focused) {
        event.preventDefault();
        focused.click();
      }
    }
  }

  search() {
    // Clear any previous selection when the user edits the input
    this.idFieldTarget.value = "";
    this.nameFieldTarget.value = this.inputTarget.value;

    clearTimeout(this._timer);
    const q = this.inputTarget.value.trim();
    if (q.length < MIN_QUERY_LENGTH) {
      this._closeDropdown();
      return;
    }

    this._timer = setTimeout(() => this._fetch(q), 250);
  }

  select(event) {
    const btn = event.currentTarget;
    this.inputTarget.value = btn.dataset.name;
    this.idFieldTarget.value = btn.dataset.id;
    this.nameFieldTarget.value = btn.dataset.name;
    this._closeDropdown();
    // Announced rather than called directly: the category control is a
    // separate concern (where does this game live on each platform) from
    // picking the name, and either can exist without the other.
    this.dispatch("selected", { detail: { id: btn.dataset.id, name: btn.dataset.name } });
  }

  async _fetch(q) {
    const requestId = ++this._requestId;
    try {
      const url = `${this.urlValue}?q=${encodeURIComponent(q)}&tenant_slug=${encodeURIComponent(this.tenantValue)}`;
      const resp = await fetch(url, {
        headers: { Accept: "application/json", "X-CSRF-Token": csrfToken() },
      });
      if (requestId !== this._requestId) return;
      if (!resp.ok) {
        this._showError();
        return;
      }
      this._hideError();
      const { games } = await resp.json();
      if (requestId !== this._requestId) return;
      this._render(games || []);
    } catch {
      if (requestId === this._requestId) this._showError();
    }
  }

  _showError() {
    this._closeDropdown();
    if (this.hasErrorTarget) this.errorTarget.hidden = false;
  }

  _hideError() {
    if (this.hasErrorTarget) this.errorTarget.hidden = true;
  }

  _items() {
    return [...this.dropdownTarget.querySelectorAll('[role="option"]')];
  }

  _moveFocus(dir) {
    const items = this._items();
    const current = items.findIndex((i) => i.getAttribute("aria-selected") === "true");
    const next = Math.max(0, Math.min(items.length - 1, current + dir));
    items.forEach((i, idx) => i.setAttribute("aria-selected", idx === next ? "true" : "false"));
    this.inputTarget.setAttribute("aria-activedescendant", items[next].id);
  }

  _render(games) {
    if (games.length === 0) {
      this._closeDropdown();
      return;
    }

    const items = games.map((g, idx) => {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.role = "option";
      btn.id = `game-option-${idx}`;
      btn.setAttribute("aria-selected", "false");
      btn.className = "w-full text-left px-3 py-2 hover:bg-base-200 text-sm truncate";
      btn.dataset.action = "click->game-search#select";
      btn.dataset.id = g.id;
      btn.dataset.name = g.name;
      btn.textContent = g.name;
      return btn;
    });

    this.dropdownTarget.replaceChildren(...items);
    this.dropdownTarget.hidden = false;
    this.inputTarget.setAttribute("aria-expanded", "true");
  }

  _closeDropdown() {
    this.dropdownTarget.replaceChildren();
    this.dropdownTarget.hidden = true;
    this.inputTarget.setAttribute("aria-expanded", "false");
    this.inputTarget.removeAttribute("aria-activedescendant");
  }

  _handleOutsideClick(event) {
    if (!this.element.contains(event.target)) this._closeDropdown();
  }
}
